import { useState } from "react";
import { motion } from "framer-motion";
import { Calculator } from "lucide-react";
import { Reveal } from "./Reveal";
import { formatCOP } from "../../lib/api";

const TIERS = [
  { min: 0, max: 4000000, rate: 0, label: "No sujeto a retención" },
  { min: 4000000, max: 10000000, rate: 0.025, label: "Tarifa referencial 2.5%" },
  { min: 10000000, max: 20000000, rate: 0.04, label: "Tarifa referencial 4%" },
  { min: 20000000, max: Infinity, rate: 0.06, label: "Tarifa referencial 6%" },
];

export const TaxCalculator = () => {
  const [income, setIncome] = useState(8000000);
  const tier = TIERS.find((t) => income >= t.min && income < t.max);
  const retention = Math.round(income * tier.rate);

  return (
    <section data-testid="interactive-calculator-widget" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="text-xs uppercase font-mono tracking-[0.28em] text-emerald-600 dark:text-emerald-400 font-semibold mb-4">Herramienta gratuita</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Simulador de retención <span className="font-serif italic text-emerald-500">en la fuente</span>
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed mt-5 max-w-lg">
            Prueba en segundos cómo calculamos en clase. Ingresa tus ingresos brutos mensuales y obtén una estimación de la retención aplicable. Así de práctico es aprender con nosotros.
          </p>
          <ul className="mt-7 space-y-2.5 text-sm text-muted-foreground">
            <li className="flex gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />Metodología paso a paso que enseñamos en Tributación</li>
            <li className="flex gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />Tablas y rangos explicados con casos reales</li>
            <li className="flex gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />Valores referenciales con fines educativos</li>
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-3xl border border-border bg-card p-8 shadow-2xl relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-500/10 blur-3xl" />
            <div className="flex items-center gap-3 mb-7">
              <span className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-500 flex items-center justify-center"><Calculator className="w-5 h-5" /></span>
              <p className="font-display font-semibold">Retención estimada 2026</p>
            </div>

            <label htmlFor="calc-income" className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Ingresos brutos mensuales</label>
            <input
              id="calc-income"
              data-testid="calculator-income-range"
              type="range"
              min="1000000"
              max="50000000"
              step="500000"
              value={income}
              onChange={(e) => setIncome(Number(e.target.value))}
              className="w-full accent-emerald-500"
            />
            <div data-testid="calculator-income-value" className="font-display font-bold text-3xl tracking-tight mt-3">{formatCOP(income)}</div>

            <div className="mt-7 grid grid-cols-2 gap-4">
              <motion.div key={tier.label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-secondary/70 p-5">
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Rango</p>
                <p data-testid="calculator-tier-label" className="font-semibold text-sm mt-1.5">{tier.label}</p>
              </motion.div>
              <motion.div key={retention} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-5">
                <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-600 dark:text-emerald-400">Retención estimada</p>
                <p data-testid="calculator-result" className="font-display font-bold text-xl mt-1.5 text-emerald-600 dark:text-emerald-400">{formatCOP(retention)}</p>
              </motion.div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-5">* Simulación con fines educativos. Las tarifas oficiales dependen de la normativa y el concepto del pago vigentes.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
